import { useAddStockNotificationMutation } from '@magentoTypes'
import { DialogContent, DialogOverlay } from '@reach/dialog'
import { useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'next-i18next'
import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { toast } from 'react-toastify'
import { Icon } from '~/elements/Icons/Icon'
import { Input } from '~/elements/Input/Input'
import { createMagentoClient } from '~/graphql/magentoClient'
import { FormValues } from '~/modules/BlogOverview/BlogOverviewModule'
import { EMAIL_PATTERN } from '~/config/constants'
import { ButtonMain } from '~/elements/Button/ButtonMain'

type Props = {
  sku: string
  open: any
  close: any
}

export const StockNotificationPopup = ({ sku, open, close }: Props) => {
  const [isSendingData, setIsSendingData] = useState(false)
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const mutationClient = createMagentoClient({ queryClient, usePost: true })
  const addStockNotification = useAddStockNotificationMutation(mutationClient, {
    onSuccess: (data) => {
      setIsSendingData(false)
      close()
      reset()

      if (data.addStockNotification)
        toast.success(t('You will receive an email as soon as this item becomes available again.'))
      else toast.error(t('Something went wrong. Please try again later.'))
    },
    onError: () => {
      setIsSendingData(false)
      close()
      reset()
      toast.error(t('Something went wrong. Please try again later.'))
    },
  })

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<FormValues>({
    defaultValues: { email: '' },
  })

  const onSubmit = handleSubmit((data) => {
    setIsSendingData(true)
    addStockNotification.mutate({
      sku,
      storeCode: process.env.NEXT_PUBLIC_STORE_CODE || 'benuta_de',
      email: data.email,
    })
  })

  return (
    <DialogOverlay
      style={{
        width: '100vw',
        height: '100vh',
        top: 0,
        left: 0,
        zIndex: 9999,
        padding: 0,
        margin: 0,
      }}
      isOpen={open}
      onDismiss={close}
    >
      <DialogContent className="relative w-[95vw] lg:w-[40vw]">
        <>
          <button
            className="absolute right-6 grid h-10 w-10 place-items-center rounded-full bg-grey-light lg:right-8"
            onClick={close}
          >
            <Icon name="cross" />
          </button>
          <div className="text-center text-h2-mobile uppercase">{t('sold out')}</div>
          <div className="my-8 text-p-normal font-extralight">
            {t('As soon as the item is available again, you will receive an email from us')}
          </div>
          <form onSubmit={onSubmit}>
            <Input
              name="email"
              type="text"
              register={register}
              placeholder={t('E-Mail Address')}
              classNameInputELement="text-p"
              autoComplete="email"
              disabled={isSendingData}
              rules={{
                required: {
                  value: true,
                  message: t('{{val}} is required', { val: t('E-Mail Address') }),
                },
                pattern: { value: EMAIL_PATTERN, message: t('Please enter a valid email') },
              }}
              classNameInputContainer="min-w-[194px] lg:min-w-[256px]"
              errors={errors}
            />
            <ButtonMain
              className="mt-4 uppercase"
              fullWidth
              disabled={isSendingData}
              variant="Secondary"
              type="submit"
            >
              {t('Notify me')}
            </ButtonMain>
          </form>
        </>
      </DialogContent>
    </DialogOverlay>
  )
}
